import React, { useContext, useMemo } from 'react';
import { MenuContext } from '../../../app/layouts/MainLayout';
import { Link } from 'react-router-dom';
import { NFTable } from '../../notas-fiscais/components/NFTable';
import { NF_STATUS_META, NF_STATUS_ORDER, formatCurrency } from '../../notas-fiscais/constants';
import { FileText, DollarSign, Clock, CheckCircle } from 'lucide-react';
import { useDashboardNotas } from '../hooks';
import type { NotaFiscal, NFStatus } from '../../../shared/types';
import { openAuthenticatedFile } from '../../../shared/lib/files';

export const DashboardFinanceiro: React.FC = () => {
  const { activeItem } = useContext(MenuContext);

  const { data, isLoading } = useDashboardNotas();
  const notas: NotaFiscal[] = data?.data ?? [];

  const stats = useMemo(() => {
    const porStatus = Object.fromEntries(NF_STATUS_ORDER.map((st) => [st, 0])) as Record<NFStatus, number>;
    for (const nf of notas) porStatus[nf.status] = (porStatus[nf.status] ?? 0) + 1;
    const totalValue = notas.reduce((acc, nf) => acc + (nf.value || 0), 0);
    // "A faturar": já enviadas e ainda não faturadas.
    const aFaturar = notas.filter((nf) => nf.status === 'em_analise' || nf.status === 'aprovada');
    return {
      total: notas.length,
      porStatus,
      totalValue,
      pendingCount: aFaturar.length,
      pendingValue: aFaturar.reduce((acc, nf) => acc + (nf.value || 0), 0),
    };
  }, [notas]);

  const handleDownloadPDF = async (nf: NotaFiscal) => {
    if (nf.pdfUrl) await openAuthenticatedFile(nf.pdfUrl);
    else alert('PDF não disponível para esta nota fiscal.');
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (activeItem === 'notas-fiscais') {
    return (
      <div className="p-6 space-y-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Todas as Notas Fiscais</h2>
          <Link to="/notas-fiscais/gestao" className="text-sm font-medium text-blue-600 hover:underline">
            Abrir gestão de NFs
          </Link>
        </div>
        <NFTable notas={notas} showCollaborator onDownloadPDF={handleDownloadPDF} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total de NFs</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <FileText className="w-8 h-8 text-blue-600 opacity-20" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Valor Total</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalValue)}</p>
            </div>
            <DollarSign className="w-8 h-8 text-green-600 opacity-20" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Em análise</p>
              <p className="text-2xl font-bold text-orange-600">{stats.porStatus.em_analise}</p>
            </div>
            <Clock className="w-8 h-8 text-orange-600 opacity-20" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Faturadas</p>
              <p className="text-2xl font-bold text-green-600">{stats.porStatus.faturada}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600 opacity-20" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribuição por Status</h3>
          <div className="space-y-3">
            {NF_STATUS_ORDER.map((status) => (
              <div key={status} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${NF_STATUS_META[status].dot}`}></div>
                  <span className="text-sm text-gray-700">{NF_STATUS_META[status].label}</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {stats.porStatus[status]}
                  {stats.total > 0 && (
                    <span className="text-xs text-gray-500 ml-2">
                      ({Math.round((stats.porStatus[status] / stats.total) * 100)}%)
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">A faturar</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Valor em análise ou aprovado:</span>
              <span className="text-lg font-bold text-orange-600">{formatCurrency(stats.pendingValue)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">NFs a faturar:</span>
              <span className="text-lg font-bold text-orange-600">{stats.pendingCount}</span>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Todas as Notas Fiscais</h2>
          <Link to="/notas-fiscais/gestao" className="text-sm font-medium text-blue-600 hover:underline">
            Abrir gestão de NFs
          </Link>
        </div>
        <NFTable notas={notas} showCollaborator onDownloadPDF={handleDownloadPDF} />
      </div>
    </div>
  );
};
